import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Typography, Button, Upload, message, Space, Alert, List, Modal } from 'antd';
import { UploadOutlined, DeleteOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { VehicleHistoryReport } from '../../types/vehicle'; // Import the central type
import { authPpiApiAxiosWithRefresh } from '../../services/api';

const { Title, Text } = Typography;
const { confirm } = Modal;

interface HistoryReportReviewProps {
  docId: string;
  vin: string;
  initialReports: VehicleHistoryReport[];
  onSubmit: (reports: VehicleHistoryReport[]) => void;
  onBack: () => void;
  onGoToVehicleDetails?: () => void;
  isManual?: boolean;
}

const HistoryReportReview: React.FC<HistoryReportReviewProps> = ({
  docId,
  vin,
  initialReports,
  onSubmit,
  onBack,
  onGoToVehicleDetails,
  isManual = false
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<VehicleHistoryReport[]>(initialReports);
  // Skip scraping entirely if there is no VIN to look up
  const [isScraping, setIsScraping] = useState(!!vin);
  const [isBuyReportModalVisible, setIsBuyReportModalVisible] = useState(false);
  const pollingStartTime = useRef<number>(Date.now());


  const fetchReports = useCallback(async () => {
    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/check-history-reports/${docId}`);
      // This endpoint now returns a simple array of URLs
      if (response.data.success) {
        const reportUrls: string[] = response.data.reports || [];
        // Transform the array of URLs into an array of VehicleHistoryReport objects
        const reportObjects: VehicleHistoryReport[] = reportUrls.map(url => ({ url }));

        if (reportObjects.length > 0) {
          setReports(reportObjects);
          setIsScraping(false);
        } else {
          // If no reports found, check if we should keep polling
          const elapsed = Date.now() - pollingStartTime.current;
          if (elapsed > 45000) { // 45 seconds timeout
            setIsScraping(false);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setIsScraping(false);
    }
  }, [docId]);

  useEffect(() => {
    const poll = setInterval(() => {
      if (isScraping) {
        fetchReports();
      } else {
        clearInterval(poll);
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [isScraping, fetchReports]);

  const handleDelete = async (reportUrl: string) => {
    confirm({
      title: 'Are you sure you want to delete this report?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      async onOk() {
        try {
          const response = await (await authPpiApiAxiosWithRefresh()).delete(`/history-report/${docId}`, {
            data: { report_url: reportUrl }
          });


          if (response.data.success) {
            message.success('Report deleted successfully');
            setReports(prevReports => prevReports.filter(report => report.url !== reportUrl));
          } else {
            throw new Error(response.data.error || 'Failed to delete report');
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'An unknown error occurred.';
          message.error(`Failed to delete report: ${msg}`);
        }
      },
    });
  };

  const handleUploadAndContinue = async () => {
    if (fileList.length === 0) {
      onSubmit(reports);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      fileList.forEach(file => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      const response = await (await authPpiApiAxiosWithRefresh()).post(`/upload-history-reports/${docId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        message.success('History reports uploaded successfully');
        await fetchReports();
        onSubmit(reports);
      } else {
        throw new Error(response.data.error || 'Failed to upload history reports');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while uploading');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className='custom-card'>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>A KicktheTires basic history report is automatically included.</Title>
          <Text type="secondary">
            We recommend adding a Carfax or Autocheck report for the most complete view of maintenance history. If you add it, we’ll use it to spot service gaps and add that into your final report.</Text>
        </div>

        {!vin && (
          <Alert
            message="No VIN provided"
            description={
              <span>
                A VIN is required to automatically search for history reports. Please go back to the
                Vehicle Details step to add a VIN.
                {onGoToVehicleDetails && (
                  <Button
                    type="link"
                    style={{ paddingLeft: 4, paddingRight: 0 }}
                    onClick={onGoToVehicleDetails}
                  >
                    Go to Vehicle Details
                  </Button>
                )}
              </span>
            }
            type="warning"
            showIcon
          />
        )}

        {vin && isScraping && <Alert message="Searching for history reports..." type="info" showIcon />}

        {reports.length > 0 && (
          <List
            bordered
            dataSource={reports}
            renderItem={(report) => (
              <List.Item
                actions={[
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(report.url)} />
                ]}
              >
                <List.Item.Meta
                  avatar={<CheckCircleOutlined />}
                  title={report.url}
                  description="History Report"
                />
              </List.Item>
            )}
          />
        )}

        {vin && !isScraping && reports.length === 0 && (
          <Alert
            message={isManual ? "No history report found" : "We didn't detect a History Report"}
            description={
              <span>
                <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                {isManual
                  ? "We could not find a history report for the VIN you entered."
                  : "Start by checking the VIN you entered. If you already have a history report, upload it below. If not, you can continue without it — but we highly recommend including one for the best results."}
              </span>
            }
            type="warning"
            showIcon
          />
        )}

        {error && <Alert message="Error" description={error} type="error" showIcon />}

        <div>
          <Title level={5}>Upload Additional Reports</Title>
          <Upload
            accept=".pdf"
            fileList={fileList}
            onChange={({ fileList: newFileList }) => setFileList(newFileList)}
            multiple
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>Upload History Report (PDF)</Button>
          </Upload>
          {!isScraping && reports.length === 0 && (
            <div style={{ marginTop: '8px' }}>
              <Text>
                Don't have one?{' '}
                <Button type="link" onClick={() => setIsBuyReportModalVisible(true)} style={{ padding: 0 }}>
                  Buy a report here.
                </Button>
              </Text>
            </div>
          )}
        </div>



        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button onClick={onBack} >Back</Button>

          <Space>
            {/* This button appears when there are reports AND no files are being uploaded */}
            {reports.length > 0 && fileList.length === 0 && (
              <Button
                type="primary"
                onClick={() => onSubmit(reports)} // Direct submit with existing reports
                disabled={isScraping} // Disabled if still fetching reports
              >
                Continue
              </Button>
            )}

            {/* This button handles uploading files */}
            {fileList.length > 0 && (
              <Button
                type="primary"
                onClick={handleUploadAndContinue}
                loading={loading}
                disabled={isScraping || loading}
              >
                Upload & Continue
              </Button>
            )}

            {/* This button appears only when there are no reports AND no files are being uploaded */}
            {reports.length === 0 && fileList.length === 0 && (
              <Button
                type="default"
                onClick={handleUploadAndContinue}
                loading={loading}
              >
                Continue Without History Report
              </Button>
            )}

            {reports.length === 0 && fileList.length === 0 && isManual && (
              <Space>
                <Button danger onClick={onGoToVehicleDetails}>
                  Start over
                </Button>
              </Space>
            )}
          </Space>
        </Space>
      </Space>
      <Modal
        title="Buy a Vehicle History Report"
        visible={isBuyReportModalVisible}
        onCancel={() => setIsBuyReportModalVisible(false)}
        footer={null}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {vin ? (
            <Text>
              You can purchase a report from one of these providers using your vehicle's VIN.
            </Text>
          ) : (
            <>
              <Text>
                A VIN is required to purchase a history report. Please contact the seller to obtain the vehicle's VIN.
              </Text>
              <Alert
                message="Tip: Once you have the VIN, you can also go back to the 'Vehicle Details' page to check for theft or salvage records."
                type="info"
                showIcon
              />
            </>
          )}
          {vin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text strong>Your VIN:</Text>
              <Text code copyable>
                {vin}
              </Text>
            </div>
          )}
          <Text>To save a report, you may need to “print to PDF” in order to save it to your computer.</Text>
          <Text>Click a provider below to purchase a report:</Text>
          <Space>
            <Button
              onClick={() => window.open(`https://www.carfax.com/vehicle-history-reports/?vin=${vin}`, '_blank')}
            >
              Buy on CarFax
            </Button>
            {/* <Button
              onClick={() => window.open(`https://www.vinaudit.com/vin-search?vin=${vin}`, '_blank')}
            >
              Buy on VinAudit
            </Button> */}
            <Button
              onClick={() => window.open(`https://www.autocheck.com/vehiclehistory/landing?vin=${vin}`, '_blank')}
            >
              Buy on AutoCheck
            </Button>
          </Space>
          <Text type="secondary" italic style={{ fontSize: '12px', textAlign: 'center', display: 'block', width: '100%' }}>
            We DO NOT earn commission from purchases made through these links.
          </Text>
        </Space>
      </Modal>
    </Card>
  );
};

export default HistoryReportReview;